import React from 'react';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  Link,
} from '@mui/material';
import { Logo } from '@horwath-htl/shared-branding';
import { Outlet, useNavigate } from 'react-router-dom';
import { brandColors } from '../theme';
import { CookieConsent } from './CookieConsent';

/**
 * Layout component for unauthenticated pages
 * Provides consistent header and footer without authentication requirements
 */
export const UnauthenticatedLayout: React.FC = () => {
  const navigate = useNavigate();

  const handleLogoClick = () => {
    navigate('/');
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', height: '100%' }}>
      {/* Header */}
      <AppBar
        position="fixed"
        sx={{
          backgroundColor: 'rgba(255,255,255,0.85)',
          backdropFilter: 'saturate(180%) blur(10px)',
          color: brandColors.indigo,
          boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
          borderBottom: '1px solid rgba(17,35,55,0.08)',
        }}
      >
        <Toolbar sx={{ gap: 2, minHeight: 72 }}>
          {/* Logo - clickable to go home */}
          <Box 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              mr: 3, 
              cursor: 'pointer',
              '& *': { cursor: 'pointer' },
            }}
            onClick={handleLogoClick}
          >
            <Logo height={35} />
          </Box>

          {/* Title */}
          <Typography
            variant="h6"
            sx={{
              flexGrow: 1,
              fontWeight: 500,
              letterSpacing: '0.3px',
              color: brandColors.indigo,
            }}
          >
            Horwath HTL US Licensee Portal
          </Typography>
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          mt: 9, // Space for the fixed AppBar
          width: '100%',
        }}
      >
        <Outlet />
      </Box>
      
      {/* Footer */}
      <Box
        component="footer"
        sx={{
          backgroundColor: '#011E41', // brandColors.indigo
          color: 'white',
          py: 2, // Thin padding
          px: 3,
          mt: 'auto',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <Typography variant="body2">
            © Copyright Barrett HTL Inc {new Date().getFullYear()} - Horwath HTL US Licensee Portal
          </Typography>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Link
              href="/privacy"
              color="inherit"
              underline="hover"
              onClick={(e) => {
                e.preventDefault();
                navigate('/privacy');
              }}
              sx={{
                fontSize: '0.875rem',
                cursor: 'pointer',
                '&:hover': {
                  color: '#F5A800', // brandColors.gold
                },
              }}
            >
              Privacy Policy
            </Link>
            
            <Link
              href="/eula"
              color="inherit"
              underline="hover"
              onClick={(e) => {
                e.preventDefault();
                navigate('/eula');
              }}
              sx={{
                fontSize: '0.875rem',
                cursor: 'pointer',
                '&:hover': {
                  color: '#F5A800', // brandColors.gold
                },
              }}
            >
              EULA
            </Link>
          </Box>
        </Box>
      </Box>
      
      {/* Cookie Consent Banner */}
      <CookieConsent />
    </Box>
  );
};